<template>
  <div class="review-landing-admin">
    <!-- Loading State -->
    <div v-if="loadingLandingPage && !business" class="container-fluid p-4">
      <div class="header-section mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="skeleton-loader" style="width: 300px; height: 32px; background: #e5e7eb; border-radius: 4px; margin-bottom: 8px;"></div>
            <div class="skeleton-loader" style="width: 400px; height: 16px; background: #e5e7eb; border-radius: 4px;"></div>
          </div>
        </div>
      </div>
      <div class="row g-4">
        <div class="col-lg-5">
          <div class="config-panel">
            <div class="skeleton-loader" style="width: 100%; height: 400px; background: #f3f4f6; border-radius: 8px;"></div>
          </div>
        </div>
        <div class="col-lg-7">
          <div class="preview-panel">
            <div class="skeleton-loader" style="width: 100%; height: 600px; background: #f3f4f6; border-radius: 8px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else-if="user && business" class="container-fluid p-3">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="h3 my-0 mx-0 text-foreground">Review Landing Page</h3>
          <p class="text-muted-foreground text-sm">Manage your customer review collection experience</p>
        </div>
        <div class="flex items-center space-x-4">
          <!-- eslint-disable vue/camelcase -->
          <router-link
            :to="{
              name: 'request-reviews',
              params: {
                unique_id: user.business.unique_id + '-' + user.business.id,
              },
            }"
            class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
            target="_blank"
            ><i class="uil uil-star"></i
            ><span class="text-sm font-medium"> Request Review</span>
          </router-link>
          <!-- eslint-enable vue/camelcase -->
        </div>
      </div>
      <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />
      <div class="main-content-wrapper">

      <!-- Landing Page URL Section -->
      <div class="url-card">
        <div class="url-card-content">
          <div class="url-left">
            <h3 class="url-title">Landing Page URL</h3>
            <div class="url-actions">
              <code v-if="!editingUniqueId" class="url-code">{{ gateUrl }}</code>
              <div v-else class="url-edit-wrapper">
                <span class="url-prefix">{{ gatePreUrl }}</span>
                <input
                  ref="uniqueIdInput"
                  v-model="editingUniqueIdValue"
                  type="text"
                  class="url-edit-input"
                  @keyup.enter="saveUniqueId"
                  @keyup.esc="cancelEditUniqueId"
                />
              </div>
              <button
                v-if="!editingUniqueId"
                type="button"
                class="url-btn"
                @click="startEditUniqueId"
              >
                <i class="uil uil-edit"></i>
                Edit
              </button>
              <button
                v-else
                type="button"
                class="url-btn"
                @click="saveUniqueId"
              >
                <i class="uil uil-check"></i>
                Save
              </button>
              <button
                v-if="editingUniqueId"
                type="button"
                class="url-btn"
                @click="cancelEditUniqueId"
              >
                <i class="uil uil-times"></i>
                Cancel
              </button>
              <button
                type="button"
                class="url-btn"
                @click="copyUrl"
              >
                <i class="uil uil-copy"></i>
                Copy
              </button>
              <a
                :href="gateUrl"
                target="_blank"
                class="url-btn"
              >
                <i class="uil uil-external-link-alt"></i>
                Preview
              </a>
            </div>
          </div>
          <div class="url-right">
            <div class="d-flex flex-column align-items-end">
              <div class="d-flex align-items-center mb-1">
                <span class="feedback-gate-label mr-2">Feedback Gate:</span>
                <b-form-checkbox
                  v-model="business.business.review_gate_enabled"
                  switch
                  size="sm"
                  class="feedback-switch"
                  @change="updateGate"
                >
                </b-form-checkbox>
                <span
                  class="feedback-badge ml-2"
                  :class="business.business.review_gate_enabled ? 'badge-enabled' : 'badge-disabled'"
                >
                  {{ business.business.review_gate_enabled ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
              <div class="d-flex align-items-center">
                <span class="feedback-gate-label mr-2">Review Sites Page:</span>
                <b-form-checkbox
                  v-model="business.business.review_gate_settings.sites_enabled"
                  switch
                  size="sm"
                  class="feedback-switch"
                  @change="updateGate"
                >
                </b-form-checkbox>
                <span
                  class="feedback-badge ml-2"
                  :class="business.business.review_gate_settings.sites_enabled !== false ? 'badge-enabled' : 'badge-disabled'"
                >
                  {{ business.business.review_gate_settings.sites_enabled !== false ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-container">
        <div class="tabs-wrapper">
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'review-sites' }"
            @click="activeTab = 'review-sites'"
          >
            Review Sites
          </button>
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'feedback-gate' }"
            @click="activeTab = 'feedback-gate'"
          >
            Feedback Gate
          </button>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="content-grid">
        <!-- Left Column: Configuration Panel -->
        <div class="config-column">
          <div class="config-card">
            <!-- Review Sites Tab Content -->
            <div v-show="activeTab === 'review-sites'" class="config-content">
              <div class="config-header">
                <i class="uil uil-globe config-icon-black"></i>
                <h3 class="config-title">Review Sites Content</h3>
              </div>

              <ValidationObserver v-if="!loadingLandingPage" ref="sitesForm" v-slot="{ handleSubmit, invalid, touched }">
                <form @submit.prevent="handleSubmit(updateSitesContent)">
                  <!-- Page Title (always editable) -->
                  <div class="form-group mb-4">
                    <label class="form-label">
                      <span class="label-text">Page Title</span>
                      <MergeFieldsGroup
                        dropdown-size="sm"
                        :show-keyword="true"
                        :show-contact-link="true"
                        :hide-title="true"
                        button-variant="outline-secondary"
                        :hide-google-links="true"
                        class="personalization-btn"
                        @selected="addFieldToInput('sitesPageTitle', $event)"
                      />
                    </label>
                    <TextInput
                      v-model="sitesContent.pageTitle"
                      class="w-100 form-input"
                      placeholder="Leave Us A Review!"
                    />
                  </div>

                  <!-- Subtitle (always editable) -->
                  <div class="form-group mb-4">
                    <label class="form-label">
                      <span class="label-text">Subtitle</span>
                      <MergeFieldsGroup
                        dropdown-size="sm"
                        :show-keyword="true"
                        :show-contact-link="true"
                        :hide-title="true"
                        button-variant="outline-secondary"
                        :hide-google-links="true"
                        class="personalization-btn"
                        @selected="addFieldToInput('sitesSubtitle', $event)"
                      />
                    </label>
                    <TextInput
                      v-model="sitesContent.subtitle"
                      class="w-100 form-input"
                      placeholder="Thanks for stopping by!"
                    />
                  </div>

                  <!-- Description Text (always editable, compact textarea) -->
                  <div class="form-group mb-4">
                    <label class="form-label">
                      <span class="label-text">Description Text</span>
                      <MergeFieldsGroup
                        dropdown-size="sm"
                        :show-keyword="true"
                        :show-contact-link="true"
                        :hide-title="true"
                        button-variant="outline-secondary"
                        :hide-google-links="true"
                        class="personalization-btn"
                        @selected="appendFieldMCE"
                      />
                    </label>
                    <b-textarea
                      v-model="sitesContent.description"
                      rows="4"
                      class="form-control text-sm"
                      placeholder="We'd love it if you could leave us a review on one of the sites below!"
                    />
                  </div>

                  <!-- Review Sites List -->
                  <div class="form-group mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h4 class="h6 mb-0">Review Sites</h4>
                      <button
                        type="button"
                        class="add-site-btn"
                        @click="addNewSite"
                      >
                        <i class="uil uil-plus"></i> Add Site
                      </button>
                    </div>

                    <!-- Add Site Form -->
                    <div v-if="addingLink" class="add-site-form bg-light rounded p-3 mb-3">
                      <div v-if="siteLink.type !== 'google'" class="form-group mb-2">
                        <label class="text-sm">Type</label>
                        <select v-model="siteLink.type" class="form-control form-control-sm" @change="updateSiteLabel">
                          <option v-for="type in availableTypes" :key="type.type" :value="type.type">
                            {{ type.label }}
                          </option>
                        </select>
                      </div>
                      <div class="form-group mb-2">
                        <TextInput
                          v-model="siteLink.url"
                          :disabled="siteLink.type === 'google'"
                          placeholder="https://"
                          label="URL"
                          class="mb-2"
                        />
                        <TextInput
                          v-model="siteLink.label"
                          placeholder="Label"
                          label="Label"
                          class="mb-2"
                        />
                        <label class="text-sm d-block mb-1">Logo</label>
                        <img
                          v-if="imgSrc(siteLink) !== null"
                          :src="imgSrc(siteLink)"
                          class="mb-2"
                          style="max-width: 50px; height: auto;"
                        />
                        <input type="file" class="form-control form-control-sm" @change="setLogo" />
                        <div class="d-flex gap-2 mt-2">
                          <b-button size="sm" variant="danger" @click="cancelSite">Cancel</b-button>
                          <b-button size="sm" variant="primary" class="save-btn" @click="addSite">
                            <b-spinner v-if="loadingUpdate" small class="mr-1" />
                            Save
                          </b-button>
                        </div>
                      </div>
                    </div>
                    

                    <!-- Sites List -->
                    <draggable
                      v-model="business.business.review_sites"
                      group="sites"
                      tag="div"
                      class="sites-list-horizontal"
                      @end="sitesOrderChanged"
                    >
                      <div
                        v-for="(site, i) in business.business.review_sites"
                        :key="i"
                        class="site-item-horizontal"
                      >
                        <div class="site-item-content-horizontal">
                          <!-- Grip Icon -->
                          <div class="grip-icon-wrapper" role="button" tabindex="0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="grip-icon">
                              <circle cx="9" cy="12" r="1"></circle>
                              <circle cx="9" cy="5" r="1"></circle>
                              <circle cx="9" cy="19" r="1"></circle>
                              <circle cx="15" cy="12" r="1"></circle>
                              <circle cx="15" cy="5" r="1"></circle>
                              <circle cx="15" cy="19" r="1"></circle>
                            </svg>
                          </div>
                          
                          <!-- Switch -->
                          <b-form-checkbox
                            v-model="site.enabled"
                            switch
                            size="sm"
                            class="site-toggle feedback-switch-style"
                            @change="updateSiteEnabled(site)"
                          />
                          
                          <!-- Icon Container -->
                          <div class="site-icon-container">
                            <div class="site-icon-inner">
                              <!-- Google SVG Icon -->
                              <svg
                                v-if="site.type === 'google'"
                                viewBox="0 0 24 24"
                                class="site-icon-img google-icon-svg"
                                fill="#4285F4"
                              >
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"></path>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
                              </svg>
                              <!-- Yelp and other icons -->
                              <img
                                v-else-if="imgSrc(site) !== null"
                                :src="imgSrc(site)"
                                :alt="site.label"
                                class="site-icon-img"
                              />
                            </div>
                          </div>
                          
                          <!-- Label with External Link -->
                          <div class="site-label-container">
                            <div class="site-label-wrapper">
                              <span class="site-label-text">{{ site.label }}</span>
                              <a :href="site.url" target="_blank" class="site-external-link" title="Open review site">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="external-link-icon">
                                  <path d="M15 3h6v6"></path>
                                  <path d="M10 14 21 3"></path>
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                </svg>
                              </a>
                            </div>
                          </div>
                          
                          <!-- Action Buttons -->
                          <div class="site-action-buttons">
                            <button class="btn-edit" title="Edit" @click="editSite(site)">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="edit-icon">
                                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                              </svg>
                            </button>
                            <button class="btn-delete" title="Delete" @click="deleteSite(site)">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="delete-icon">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                <line x1="10" x2="10" y1="11" y2="17"></line>
                                <line x1="14" x2="14" y1="11" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </draggable>

                    <div class="d-flex justify-content-end mt-3">
                      <b-button
                        type="submit"
                        variant="primary"
                        class="save-btn"
                        :disabled="loadingUpdate || (invalid && touched)"
                      >
                        <b-spinner v-if="loadingUpdate" small class="mr-2" />
                        Save
                      </b-button>
                    </div>
                  </div>
                </form>
              </ValidationObserver>
            </div>

            <!-- Feedback Gate Tab Content -->
            <div v-show="activeTab === 'feedback-gate'" class="config-content">
              <div class="feedback-gate-card">
                <div class="feedback-gate-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feedback-gate-icon">
                    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                  </svg>
                  <h3 class="feedback-gate-title">Feedback Gate Content</h3>
                </div>

                <ValidationObserver v-if="!loadingLandingPage" ref="gateForm" v-slot="{ handleSubmit, invalid, touched }">
                  <form class="feedback-gate-form" @submit.prevent="handleSubmit(updateGate)">
                    <div class="gate-content-section">
                      <h4 class="gate-content-heading">Gate Content</h4>
                      
                      <div class="space-y-6">
                        <!-- Feedback Type (single option: no dropdown) -->
                        <div class="form-field-group">
                          <label class="field-label">Feedback Type</label>
                          <p class="feedback-type-value-static">Thumbs Up/Down</p>
                          <p class="field-hint">Customers click thumbs up or down buttons</p>
                        </div>

                        <!-- Body Title (always editable) -->
                        <div class="form-field-group">
                          <label class="field-label">Body Title</label>
                          <p class="field-hint">Use [first_name] for personalization</p>
                          <TextInput
                            v-model="business.business.review_gate_settings.body_title"
                            class="w-100 form-input"
                            placeholder="[first_name] How was your recent experience?"
                          />
                        </div>

                        <!-- Body Text (always editable, compact textarea) -->
                        <div class="form-field-group">
                          <label class="field-label">Body Text</label>
                          <p class="field-hint">Main message to customers</p>
                          <b-textarea
                            v-model="business.business.review_gate_settings.body_text"
                            rows="4"
                            class="form-control text-sm"
                            placeholder="Thanks for choosing us! Would you please take a moment to rate your experience? Your feedback is highly appreciated, and your response will help us serve you even better in the future."
                          />
                        </div>

                        <!-- Good and Bad Experience Buttons (always editable) -->
                        <div class="buttons-grid">
                          <!-- Good Experience Button -->
                          <div class="form-field-group">
                            <label class="field-label">Good Experience Button</label>
                            <TextInput
                              v-model="business.business.review_gate_settings.good_label"
                              class="w-100 form-input"
                              placeholder="I had a good experience"
                            />
                          </div>

                          <!-- Bad Experience Button -->
                          <div class="form-field-group">
                            <label class="field-label">Bad Experience Button</label>
                            <TextInput
                              v-model="business.business.review_gate_settings.bad_label"
                              class="w-100 form-input"
                              placeholder="I had a bad experience"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex justify-content-end gap-2 mt-4">
                      <b-button variant="outline-secondary" @click="cancelGate">
                        <i class="uil uil-times"></i> Cancel
                      </b-button>
                      <b-button
                        type="submit"
                        variant="primary"
                        class="save-btn"
                        :disabled="loadingUpdate || (invalid && touched)"
                      >
                        <b-spinner v-if="loadingUpdate" small class="mr-2" />
                        Save
                      </b-button>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Preview Panel -->
        <div class="preview-column">
          <div class="preview-card">
            <div v-show="activeTab === 'review-sites'" class="preview-header">
              <i class="uil uil-eye preview-icon-black"></i>
              <h3 class="preview-title">Review Sites Preview</h3>
            </div>
            
            <!-- Loading state for preview -->
            <div v-if="loadingLandingPage" class="text-center py-5">
              <b-spinner></b-spinner>
              <p class="text-muted mt-2">Loading preview...</p>
            </div>

            <!-- Preview Tabs for Feedback Gate -->
            <div v-show="activeTab === 'feedback-gate'" class="gate-preview-card">
              <div class="gate-preview-header">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gate-preview-icon">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <h3 class="gate-preview-title">Gate Preview</h3>
              </div>
              <div class="gate-preview-tabs-wrapper">
                <div class="gate-preview-tabs">
                  <button
                    type="button"
                    class="gate-tab-button"
                    :class="{ active: previewTab === 'feedback' }"
                    @click="previewTab = 'feedback'"
                  >
                    Feedback
                  </button>
                  <button
                    type="button"
                    class="gate-tab-button"
                    :class="{ active: previewTab === 'positive' }"
                    @click="previewTab = 'positive'"
                  >
                    Positive
                  </button>
                  <button
                    type="button"
                    class="gate-tab-button"
                    :class="{ active: previewTab === 'negative' }"
                    @click="previewTab = 'negative'"
                  >
                    Negative
                  </button>
                </div>
              </div>
            </div>

            <!-- Preview Content -->
            <div v-if="!loadingLandingPage && business" class="preview-content-wrapper">
              <!-- Only render preview when tab is active to reduce initial load -->
              <ReviewLandingPagePreview
                v-if="business.business"
                :key="`${activeTab}-${previewTab}-${business.business.id || Date.now()}`"
                :business="business"
                :active-tab="activeTab"
                :preview-tab="previewTab"
                :sites-content="sitesContent"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import Vue from 'vue'
import '../../../design/custom/components/_dashboard.scss'
import draggable from 'vuedraggable'
import MergeFieldsGroup from '@components/Business/Automations/MergeFieldsGroup.vue'
import { reviewGatePublicBaseUrl } from '@utils/reviewGatePublicUrl'

export default {
  components: {
    MergeFieldsGroup,
    draggable,
    // Lazy load preview component for better initial load performance
    ReviewLandingPagePreview: () => import('./ReviewLandingPagePreview.vue'),
  },
  data() {
    return {
      user: null,
      business: null,
      loadingLandingPage: false,
      loadingUpdate: false,
      activeTab: 'review-sites',
      previewTab: 'feedback',
      editingUniqueId: false,
      editingUniqueIdValue: '',
      addingLink: false,
      logoUpload: null,
      placeId: null,
      siteLink: {
        url: '',
        logo_path: '',
        type: 'custom',
        label: '',
      },
      sitesContent: {
        pageTitle: '',
        subtitle: '',
        description: '',
      },
      reviewSites: [
        { type: 'facebook', label: 'Facebook' },
        { type: 'google', label: 'Google' },
        { type: 'yelp', label: 'Yelp' },
        { type: 'custom', label: 'Custom' },
      ],
    }
  },
  computed: {
    gatePreUrl() {
      return reviewGatePublicBaseUrl()
    },
    gateUrl() {
      return this.gatePreUrl + (this.business?.business?.unique_id || '')
    },
    availableTypes() {
      // Optimize: Cache check and early return
      if (!this.business?.business?.review_sites) return this.reviewSites
      
      // Use Set for O(1) lookup instead of array includes
      const existingTypes = new Set(
        this.business.business.review_sites
          .map((site) => site.type)
          .filter((type) => type !== 'custom')
      )
      
      return this.reviewSites.filter((type) => !existingTypes.has(type.type))
    },
  },
  watch: {},
  mounted() {
    this.user = Vue.util.extend({}, this.$store.getters['auth/user'])
    // Start loading immediately without waiting for nextTick
    this.getWidget()
  },
  methods: {
    getWidget() {
      this.loadingLandingPage = true
      this.$store
        .dispatch('landingPage/getReviewGate', {
          business_id: this.user.business.unique_id,
          query_params: 'is_admin=true',
        })
        .then((res) => {
          this.business = res.reviewGate
          // Set loading to false immediately to show UI structure
          this.loadingLandingPage = false
          
          // Ensure new sites_enabled flag exists for older businesses
          if (
            this.business &&
            this.business.business &&
            this.business.business.review_gate_settings &&
            typeof this.business.business.review_gate_settings.sites_enabled === 'undefined'
          ) {
            this.$set(this.business.business.review_gate_settings, 'sites_enabled', true)
          }
          
          // Initialize content immediately but defer heavy operations
          this.initializeSitesContent()
          this.initializeReviewSites()
          
          // Defer editor loading to avoid blocking initial render
          this.$nextTick(() => {
            // Use requestAnimationFrame for smoother loading
            requestAnimationFrame(() => {
              setTimeout(() => {
                this.editorReady = true
              }, 500) // Increased delay to ensure UI is fully rendered
            })
          })
        })
        .catch((error) => {
          console.error(error)
          this.loadingLandingPage = false
        })
    },
    initializeSitesContent() {
      // Parse sites_body_text to extract page title, subtitle, and description
      const bodyText = this.business.business.review_gate_settings.sites_body_text || ''
      
      // Optimize: Use simple regex parsing instead of DOM manipulation for better performance
      if (bodyText && bodyText.trim()) {
        // Extract text from HTML using regex (faster than DOM manipulation)
        const textOnly = bodyText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
        const lines = textOnly.split(/\n/).filter(line => line.trim())
        
        // Try to parse HTML content with minimal DOM manipulation
        try {
          // Use a lightweight approach - check for common patterns first
          const titleMatch = bodyText.match(/<p[^>]*><strong[^>]*><span[^>]*>([^<]+)<\/span><\/strong><\/p>/i)
          const subtitleMatch = bodyText.match(/<p[^>]*><span[^>]*>([^<]+)<\/span><\/p>/gi)
          
          if (titleMatch && titleMatch[1]) {
            this.sitesContent.pageTitle = titleMatch[1].trim() || 'Leave Us A Review!'
          } else if (lines.length > 0) {
            this.sitesContent.pageTitle = lines[0].trim() || 'Leave Us A Review!'
          } else {
            this.sitesContent.pageTitle = 'Leave Us A Review!'
          }
          
          if (subtitleMatch && subtitleMatch.length > 0) {
            const subtitleText = subtitleMatch[0].replace(/<[^>]*>/g, '').trim()
            this.sitesContent.subtitle = subtitleText || 'Thanks for stopping by!'
          } else if (lines.length > 1) {
            this.sitesContent.subtitle = lines[1].trim() || 'Thanks for stopping by!'
          } else {
            this.sitesContent.subtitle = 'Thanks for stopping by!'
          }
          
          // Description is everything else
          if (lines.length > 2) {
            this.sitesContent.description = lines.slice(2).join(' ').trim() || "We'd love it if you could leave us a review on one of the sites below!"
          } else {
            this.sitesContent.description = textOnly.replace(this.sitesContent.pageTitle, '').replace(this.sitesContent.subtitle, '').trim() || "We'd love it if you could leave us a review on one of the sites below!"
          }
        } catch (e) {
          // Fallback to simple defaults
          this.sitesContent.pageTitle = 'Leave Us A Review!'
          this.sitesContent.subtitle = 'Thanks for stopping by!'
          this.sitesContent.description = "We'd love it if you could leave us a review on one of the sites below!"
        }
      } else {
        // Default values
        this.sitesContent.pageTitle = 'Leave Us A Review!'
        this.sitesContent.subtitle = 'Thanks for stopping by!'
        this.sitesContent.description = "We'd love it if you could leave us a review on one of the sites below!"
      }
    },
    initializeReviewSites() {
      // Optimize: Early return if no business data
      if (!this.business?.business) return
      
      // Initialize review sites if empty
      if (!this.business.business.review_sites || !this.business.business.review_sites.length) {
        // Determine place ID efficiently
        this.placeId = 
          (this.user.business.google_maps_url?.includes(this.user.business.place_id) && this.user.business.aux_place_id)
            ? this.user.business.aux_place_id
            : this.user.business.place_id || this.business.business?.location?.place_id
        
        if (this.placeId) {
          this.$set(this.business.business, 'review_sites', [
            {
              url: 'https://search.google.com/local/writereview?placeid=' + this.placeId,
              logo_path: '',
              type: 'google',
              label: 'Google',
              enabled: true,
            },
          ])
        }
      }
      
      // Ensure all sites have enabled property - use batch update
      if (this.business.business.review_sites) {
        const sites = this.business.business.review_sites
        for (let i = 0; i < sites.length; i++) {
          if (sites[i].enabled === undefined) {
            this.$set(sites[i], 'enabled', true)
          }
        }
      }
    },
    copyUrl() {
      navigator.clipboard.writeText(this.gateUrl).then(() => {
        this.$bvToast.toast('URL copied to clipboard', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      })
    },
    startEditUniqueId() {
      this.editingUniqueIdValue = this.business?.business?.unique_id || ''
      this.editingUniqueId = true
      this.$nextTick(() => {
        if (this.$refs.uniqueIdInput) {
          this.$refs.uniqueIdInput.focus()
          this.$refs.uniqueIdInput.select()
        }
      })
    },
    saveUniqueId() {
      if (this.editingUniqueIdValue && this.business?.business) {
        this.business.business.unique_id = this.editingUniqueIdValue.trim()
        // Optionally save to backend here
        this.editingUniqueId = false
        this.$bvToast.toast('URL updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      }
    },
    cancelEditUniqueId() {
      this.editingUniqueId = false
      this.editingUniqueIdValue = ''
    },
    addFieldToInput(refName, field) {
      // Only touch the reactive model; let Vue keep inputs in sync
      if (refName === 'sitesPageTitle') {
        const current = this.sitesContent.pageTitle || ''
        this.sitesContent.pageTitle = current + field
      } else if (refName === 'sitesSubtitle') {
        const current = this.sitesContent.subtitle || ''
        this.sitesContent.subtitle = current + field
      }
    },
    appendFieldMCE(field) {
      // Append personalization token to the description model
      const current = this.sitesContent.description || ''
      this.sitesContent.description = current + field
    },
    updateSitesContent() {
      this.loadingUpdate = true
      // Build HTML from sitesContent
      const html = `
        <p style="text-align: center;"><strong><span style="font-size: 14pt;">${this.sitesContent.pageTitle}</span></strong></p>
        <p style="text-align: center;"><span style="font-size: 14pt;">${this.sitesContent.subtitle}</span></p>
        <p style="text-align: center;"><span style="font-size: 14pt;">${this.sitesContent.description}</span></p>
      `
      this.business.business.review_gate_settings.sites_body_text = html
      this.updateGate()
    },
    updateGate() {
      this.loadingUpdate = true
      const business = {
        ...this.business.business,
        review_gate_settings: this.business.business.review_gate_settings,
      }
      this.$store
        .dispatch('business/gateUpdate', business)
        .then(() => {
          this.loadingUpdate = false
          this.$bvToast.toast('Settings saved successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          })
        })
        .catch((err) => {
          if (err.response && err.response.data && err.response.data.errors) {
            this.$refs.gateForm.setErrors(err.response.data.errors)
          }
          this.loadingUpdate = false
        })
    },
    cancelGate() {
      // Reset form or reload data
      this.getWidget()
    },
    updateSiteLabel() {
      if (this.siteLink.label === '' || !this.siteLink.hasOwnProperty('label')) {
        const siteType = this.reviewSites.find((s) => s.type === this.siteLink.type)
        if (siteType) {
          this.siteLink.label = siteType.label
        }
      }
    },
    addNewSite() {
      this.addingLink = true
      this.siteLink = {
        url: '',
        logo_path: '',
        type: 'custom',
        label: '',
      }
    },
    editSite(site) {
      this.siteLink = JSON.parse(JSON.stringify(site))
      this.addingLink = true
    },
    deleteSite(site) {
      this.business.business.review_sites = this.business.business.review_sites.filter(
        (item) => item.url !== site.url
      )
      this.reOrderSites()
    },
    cancelSite() {
      this.addingLink = false
      this.logoUpload = null
      this.siteLink = {
        url: '',
        logo_path: '',
        type: 'custom',
        label: '',
      }
    },
    setLogo(event) {
      this.logoUpload = event.target.files[0]
    },
    sitesOrderChanged() {
      if (this.business.business.review_sites.length > 1) {
        this.reOrderSites()
      }
    },
    updateSiteEnabled(site) {
      // Auto-save when toggle changes
      this.reOrderSites()
    },
    reOrderSites() {
      this.loadingUpdate = true
      this.$store
        .dispatch('business/reOrderReviewSite', {
          review_sites: JSON.stringify(this.business.business.review_sites) ?? [],
          business_id: this.business.business.id,
        })
        .then((resp) => {
          this.business.business = resp.data
          this.loadingUpdate = false
        })
        .catch((err) => {
          console.error(err)
          this.loadingUpdate = false
        })
    },
    addSite() {
      this.loadingUpdate = true
      const uploadForm = new FormData()
      if (this.logoUpload !== null && this.logoUpload !== '') {
        uploadForm.append('logo', this.logoUpload)
      }
      uploadForm.append('site_url', this.siteLink.url)
      uploadForm.append('site_logo', this.siteLink.logo_path)
      uploadForm.append('site_type', this.siteLink.type)
      uploadForm.append('site_label', this.siteLink.label)
      uploadForm.append('review_sites', JSON.stringify(this.business.business.review_sites) ?? [])
      uploadForm.append('business_id', this.business.business.id)

      this.$store
        .dispatch('business/addUpdateReviewSite', uploadForm)
        .then((resp) => {
          this.business.business = resp.data
          this.loadingUpdate = false
          this.addingLink = false
          this.siteLink = {
            url: '',
            logo_path: '',
            type: 'custom',
            label: '',
          }
          this.logoUpload = null
        })
        .catch((err) => {
          if (err.response && err.response.data) {
            if (err.response.data.errors) {
              this.$refs.sitesForm.setErrors(err.response.data.errors)
            }
            // Show user-friendly error message for Imagick/Image processing errors
            const errorMessage = err.response.data.message || err.response.data.error || 'An error occurred while uploading the logo'
            if (errorMessage.includes('Imagick') || errorMessage.includes('image') || errorMessage.includes('driver')) {
              this.$bvToast.toast('Image processing error: The server requires Imagick PHP extension. Please contact your system administrator to install it, or configure the application to use GD instead.', {
                title: 'Upload Error',
                variant: 'danger',
                solid: true,
                autoHideDelay: 10000,
              })
            } else {
              this.$bvToast.toast(errorMessage, {
                title: 'Error',
                variant: 'danger',
                solid: true,
              })
            }
          } else {
            this.$bvToast.toast('An error occurred while saving the site. Please try again.', {
              title: 'Error',
              variant: 'danger',
              solid: true,
            })
          }
          this.loadingUpdate = false
        })
    },
    imgSrc(site) {
      let src = null
      if (
        site.type === 'google' &&
        (!site.hasOwnProperty('logo_path') ||
          site.logo_path === '' ||
          site.logo_path === 'null')
      ) {
        src = 'https://rengine.sfo3.cdn.digitaloceanspaces.com/google-icon-100.png'
      } else if (
        site.type === 'facebook' &&
        (!site.hasOwnProperty('logo_path') ||
          site.logo_path === '' ||
          site.logo_path === 'null')
      ) {
        src = 'https://rengine.sfo3.cdn.digitaloceanspaces.com/facebook-100.png'
      } else if (
        site.type === 'yelp' &&
        (!site.hasOwnProperty('logo_path') ||
          site.logo_path === '' ||
          site.logo_path === 'null')
      ) {
        src = 'https://rengine.sfo3.cdn.digitaloceanspaces.com/yelp-logo-100.png'
      } else if (site.logo_path && site.logo_path !== '' && site.logo_path !== 'null') {
        src = this.user.business.cdn_url + site.logo_path
      }
      return src
    },
  },
}
</script>

<style lang="scss" scoped>
.review-landing-admin {
  min-height: 100vh;
  background: white;
}


.header-section {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 32px;
  margin: -24px -24px 24px -24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .page-title {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 4px 0;
  }
  
  .page-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }
  
  .request-review-btn {
    background-color: rgb(36, 99, 235) !important;
    color: #ffffff !important;
  }
}

.url-card {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid rgba(229, 231, 235, 0.5);
  
  .url-card-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }
  
  .url-left {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    
    .url-title {
      font-size: 18px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }
    
    .url-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    
    .url-code {
      background: #f3f4f6;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 14px;
      font-family: monospace;
      color: #3b82f6;
      max-width: 400px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .url-edit-wrapper {
      display: flex;
      align-items: center;
      background: #f3f4f6;
      padding: 8px 12px;
      border-radius: 6px;
      gap: 4px;
      
      .url-prefix {
        font-size: 14px;
        font-family: monospace;
        color: #6b7280;
        white-space: nowrap;
      }
      
      .url-edit-input {
        border: none;
        outline: none;
        background: transparent;
        font-size: 14px;
        font-family: monospace;
        color: #3b82f6;
        flex: 1;
        min-width: 100px;
        
        &:focus {
          outline: none;
        }
      }
    }
    
    .url-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      border: 1px solid #e5e7eb;
      background: white;
      color: #374151;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      text-decoration: none;
      
      &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
        color: #1f2937;
      }
      
      i {
        font-size: 14px;
      }
    }
  }
  
  .url-right {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .feedback-gate-label {
      font-size: 14px;
      color: #6b7280;
    }
    
    .feedback-switch {
      margin: 0;
      
      :deep(.custom-control-input:checked ~ .custom-control-label::before) {
        background-color: #3b82f6;
        border-color: #3b82f6;
      }
      
      :deep(.custom-control-input:not(:checked) ~ .custom-control-label::before) {
        background-color: #e5e7eb;
        border-color: #e5e7eb;
      }
      
      :deep(.custom-switch .custom-control-label::after) {
        background-color: white;
      }
      
      :deep(.custom-switch) {
        padding-left: 2.5rem;
        min-height: 1.5rem;
      }
      
      :deep(.custom-control-label) {
        padding-top: 0;
        padding-left: 0.5rem;
        cursor: pointer;
        line-height: 1.5rem;
      }
      
      :deep(.custom-control-label::before) {
        width: 2.5rem;
        height: 1.25rem;
        border-radius: 0.625rem;
        top: 0.125rem;
        left: -2.5rem;
      }
      
      :deep(.custom-control-label::after) {
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        top: 0.25rem;
        left: -2.375rem;
        transition: transform 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
      }
      
      :deep(.custom-control-input:checked ~ .custom-control-label::after) {
        transform: translateX(1.25rem);
      }
    }
    
    .feedback-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 9999px;
      padding: 0 10px;
      font-size: 12px;
      font-weight: 600;
      height: 1.5rem;
      line-height: 1.5rem;
      
      &.badge-enabled {
        border: 1px solid transparent;
        background: #3b82f6;
        color: white;
      }
      
      &.badge-disabled {
        border: 1px solid #e5e7eb;
        background: transparent;
        color: #6b7280;
      }
    }
  }
}

.tabs-container {
  margin-bottom: 24px;
  
  .tabs-wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    width: 100%;
    background: #f3f4f6;
    padding: 4px;
    border-radius: 6px;
    
    .tab-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 6px 12px;
      font-size: 14px;
      font-weight: 500;
      color: #6b7280;
      background: transparent;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        color: #374151;
      }
      
      &.active {
        background: white;
        color: #1f2937;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        font-weight: 500;
      }
    }
  }
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  
  @media (min-width: 1024px) {
    grid-template-columns: 40% 60%;
  }
}

.config-column {
  .config-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    height: fit-content;
    position: sticky;
    top: 20px;
  }
  
  .config-content {
    padding: 24px;
  }
  
  .config-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0px 24px 0 0px;
    margin-bottom: 24px;
    
    i {
      font-size: 20px;
      color: #3b82f6;
    }
    
    .config-icon-black {
      color: #1f2937 !important;
    }
    
    .config-title {
      font-size: 20px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }
  }
}

.preview-column {
  .preview-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  
  .preview-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 24px 24px 0 24px;
    margin-bottom: 24px;
    
    i {
      font-size: 20px;
      color: #3b82f6;
    }
    
    .preview-icon-black {
      color: #1f2937 !important;
    }
    
    .preview-title {
      font-size: 20px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }
  }
  
  .preview-content-wrapper {
    padding: 0 24px 24px 24px;
    
    :deep(.review-landing-preview) {
      background: #f3f4f6;
      border-radius: 8px;
      padding: 24px;
      min-height: 500px;
    }
  }
}

.gate-preview-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
  
  .gate-preview-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 24px 24px 0 24px;
    margin-bottom: 16px;
    
    .gate-preview-icon {
      width: 20px;
      height: 20px;
      color: #1f2937;
      flex-shrink: 0;
    }
    
    .gate-preview-title {
      font-size: 24px;
      font-weight: 600;
      line-height: 1.2;
      color: #1f2937;
      margin: 0;
    }
  }
  
  .gate-preview-tabs-wrapper {
    padding: 0 24px 24px 24px;
  }
  
  .gate-preview-tabs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
    background: #f3f4f6;
    padding: 4px;
    border-radius: 6px;
    height: 40px;
    
    .gate-tab-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
      padding: 6px 12px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 4px;
      transition: all 0.2s;
      background: transparent;
      border: none;
      color: #6b7280;
      cursor: pointer;
      
      &:hover {
        color: #1f2937;
      }
      
      &.active {
        background: white;
        color: #1f2937;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      }
    }
  }
}

.preview-content {
  min-height: 400px;
}

.sites-list-horizontal {
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  .site-item-horizontal {
    margin-bottom: 0;
    
    .site-item-content-horizontal {
      display: flex;
      align-items: center;
      gap: 0;
      padding: 16px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      transition: all 0.2s ease;
      width: 100%;

      &:hover {
        border-color: #d1d5db;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
    }
    
    .grip-icon-wrapper {
      cursor: grab;
      padding: 4px;
      border-radius: 4px;
      transition: background-color 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 6px;
      
      &:hover {
        background: rgba(156, 163, 175, 0.1);
        cursor: grabbing;
      }
      
      .grip-icon {
        width: 16px;
        height: 16px;
        color: #9ca3af;
      }
    }
    
    .site-icon-container {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-right: 6px;
      
      .site-icon-inner {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        .site-icon-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          
          &.google-icon-svg {
            width: 100%;
            height: 100%;
          }
        }
      }
    }
    
    .site-label-container {
      flex: 1;
      min-width: 0;
      
      .site-label-wrapper {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-right: 8px;
        
        .site-label-text {
          font-weight: 500;
          font-size: 14px;
          color: #1f2937;
        }
        
        .site-external-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
          color: #6b7280;
          
          &:hover {
            background: #f3f4f6;
            color: #3b82f6;
          }
          
          .external-link-icon {
            width: 14px;
            height: 14px;
          }
        }
      }
    }
    
    .site-action-buttons {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
      
      .btn-edit,
      .btn-delete {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 8px;
        border: 1px solid #e5e7eb;
        background: white;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }
        
        .edit-icon,
        .delete-icon {
          width: 14px;
          height: 14px;
          color: #6b7280;
        }
      }
      
      .btn-delete {
        &:hover {
          background: #fef2f2;
          border-color: #ef4444;
          
          .delete-icon {
            color: #ef4444;
          }
        }
      }
    }
    
    .site-toggle.feedback-switch-style {
      margin: 0;
      margin-right: 3px;
      
      :deep(.custom-control-input:checked ~ .custom-control-label::before) {
        background-color: #3b82f6;
        border-color: #3b82f6;
      }
      
      :deep(.custom-control-input:not(:checked) ~ .custom-control-label::before) {
        background-color: #e5e7eb;
        border-color: #e5e7eb;
      }
      
      :deep(.custom-switch .custom-control-label::after) {
        background-color: white;
      }
      
      :deep(.custom-switch) {
        padding-left: 2.5rem;
        min-height: 1.5rem;
      }
      
      :deep(.custom-control-label) {
        padding-top: 0;
        padding-left: 0.5rem;
        cursor: pointer;
        line-height: 1.5rem;
      }
      
      :deep(.custom-control-label::before) {
        width: 2.5rem;
        height: 1.25rem;
        border-radius: 0.625rem;
        top: 0.125rem;
        left: -2.5rem;
      }
      
      :deep(.custom-control-label::after) {
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        top: 0.25rem;
        left: -2.375rem;
        transition: transform 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
      }
      
      :deep(.custom-control-input:checked ~ .custom-control-label::after) {
        transform: translateX(1.25rem);
      }
    }
    
    .site-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      min-width: 0;
      
      .site-logo-small {
        width: 40px;
        height: 40px;
        object-fit: contain;
        flex-shrink: 0;
      }
      
      .site-details {
        flex: 1;
        min-width: 0;
        
        .site-name-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .site-name-small {
          font-weight: 500;
          font-size: 14px;
          color: #1f2937;
        }
        
        .site-link-inline {
          display: inline-flex;
          align-items: center;
          color: #6b7280;
          text-decoration: none;
          padding: 2px 4px;
          border-radius: 4px;
          transition: all 0.2s;
          
          &:hover {
            background: #f3f4f6;
            color: #3b82f6;
          }
          
          i {
            font-size: 14px;
          }
        }
      }
    }
    
    .site-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .btn-action {
        background: transparent;
        border: 1px solid #e5e7eb;
        color: #6b7280;
        padding: 4px 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s;
        
        &:hover {
          background: #f9fafb;
          border-color: #d1d5db;
          color: #1f2937;
        }
        
        &.btn-action-small {
          padding: 2px 6px;
          width: 32px;
          height: 32px;
        }
        
        &.text-danger:hover {
          background: #fef2f2;
          border-color: #ef4444;
          color: #ef4444;
        }
      }
    }
  }
}

.add-site-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #2563eb;
    color: white;
  }
  
  i {
    font-size: 14px;
  }
}

.save-btn {
  border-radius: 6px !important;
  background: #3b82f6 !important;
  border-color: #3b82f6 !important;
  
  &:hover:not(:disabled) {
    background: #2563eb !important;
    border-color: #2563eb !important;
  }
  
  &:disabled {
    opacity: 0.6;
  }
}

.add-site-form {
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 16px;
  background: #f9fafb;
}

.personalization-btn {
  :deep(button) {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    border-color: #e5e7eb;
    color: #374151;
    border: 1px solid #e5e7eb;
    width: 100%;
    
    &::before {
      content: '';
      display: inline-block;
      width: 14px;
      height: 14px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2'%3E%3C/path%3E%3Ccircle cx='12' cy='7' r='4'%3E%3C/circle%3E%3C/svg%3E");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      flex-shrink: 0;
    }
    
    i:last-child {
      margin-left: auto;
      flex-shrink: 0;
    }
  }
}

.form-group {
  .form-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    
    .label-text {
      font-size: 14px;
      font-weight: 500;
      color: #6b7280;
    }
  }
  
  .form-hint {
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 8px;
    margin-top: -4px;
  }
  
  .dashed-input {
    padding: 12px;
    border: 1px dashed rgba(107, 114, 128, 0.3);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    background: white;
    min-height: 40px;
    display: flex;
    align-items: center;
    
    &:hover {
      background: rgba(243, 244, 246, 0.2);
      border-color: rgba(107, 114, 128, 0.5);
    }
    
    &.dashed-textarea {
      min-height: 80px;
      align-items: flex-start;
    }
    
    span {
      color: #1f2937;
      font-size: 14px;
    }
  }
  
  .hidden-input {
    display: none;
  }
  
  .dashed-input-small {
    padding: 8px;
    min-height: 36px;
  }
}

.feedback-gate-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  .feedback-gate-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 24px 24px 0 24px;
    
    .feedback-gate-icon {
      width: 20px;
      height: 20px;
      color: #1f2937;
      flex-shrink: 0;
    }
    
    .feedback-gate-title {
      font-size: 24px;
      font-weight: 600;
      line-height: 1.2;
      color: #1f2937;
      margin: 0;
    }
  }
  
  .feedback-gate-form {
    padding: 24px;
  }
  
  .gate-content-section {
    .gate-content-heading {
      font-weight: 500;
      font-size: 16px;
      color: #1f2937;
      margin-bottom: 24px;
    }
  }
  
  .space-y-6 {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  
  .form-field-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    .field-label {
      font-size: 14px;
      color: #6b7280;
      margin-bottom: 0;
    }
    
    .field-label-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0;
    }
    
    .field-hint {
      font-size: 12px;
      color: #9ca3af;
      margin: 0;
    }
    
    .feedback-type-value-static {
      font-size: 14px;
      color: #1f2937;
      margin: 8px 0 0 0;
      font-weight: 500;
    }
    
    .feedback-type-select {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 40px;
      padding: 0 12px;
      border: 1px solid #e5e7eb;
      background: white;
      border-radius: 6px;
      margin-top: 8px;
      cursor: default;
      
      .feedback-type-value {
        font-size: 14px;
        color: #1f2937;
      }
      
      .chevron-icon {
        width: 16px;
        height: 16px;
        color: #9ca3af;
        opacity: 0.5;
      }
    }
  }
  
  .buttons-grid {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.skeleton-loader {
  animation: skeleton-loading 1.5s ease-in-out infinite;
}

@keyframes skeleton-loading {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}
</style>

